import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Dialog,
  Typography,
  Button,
  TextField,
  Avatar,
  ListItemAvatar,
  ListItemText,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Switch,
  InputAdornment,
  List,
  ListItem,
  Tabs,
  Tab,
  Fade,
  Paper,
  Tooltip
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { Capsule } from '../types';
import ImageIcon from '@mui/icons-material/Image';
import CheckIcon from '@mui/icons-material/Check';
import { PersonOff, Search, CheckCircle, QrCode2, ContentCopy } from '@mui/icons-material';
import { motion, AnimatePresence, useAnimation, PanInfo } from 'framer-motion';
import { fetchAuthSession } from '@aws-amplify/auth';

export const ConfirmationDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  confirmColor?: 'primary' | 'error';
}> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  confirmColor = 'primary',
}) => (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper',
          backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          {message}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'background.default',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onConfirm}
            color={confirmColor}
            sx={{
              bgcolor: confirmColor === 'error' ? 'error.main' : 'primary.main',
              '&:hover': {
                bgcolor: confirmColor === 'error' ? 'error.dark' : 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            {confirmText}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );

export const DeleteCapsuleDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ open, onClose, onConfirm }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    title="Delete Time Capsule"
    message="Are you sure you want to delete this time capsule? This action cannot be undone and all memories will be permanently lost."
    confirmText="Delete"
    confirmColor="error"
  />
);

export const RemoveAccessDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ open, onClose, onConfirm }) => (
  <ConfirmationDialog
    open={open}
    onClose={onClose}
    onConfirm={onConfirm}
    title="Remove Access"
    message="Are you sure you want to remove your access to this time capsule? You won't be able to view or contribute to it anymore."
    confirmText="Remove Access"
    confirmColor="error"
  />
);

export const AddAccessDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  currentCapsule: Capsule | null;
  user: any;
  friends: any[];
  selectedUsers: string[];
  setSelectedUsers: (users: string[]) => void;
  handleAddAccess: () => void;
}> = ({
  open,
  onClose,
  currentCapsule,
  user,
  friends,
  selectedUsers,
  setSelectedUsers,
  handleAddAccess,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTab, setSelectedTab] = useState(1);
    const [copied, setCopied] = useState(false);
    const [pin, setPin] = useState('');
    const [pinEnabled, setPinEnabled] = useState(!!currentCapsule?.code);
    const [currentPin, setCurrentPin] = useState(currentCapsule?.code || '');
    const tabContentRef = useRef<HTMLDivElement>(null);
    const controls = useAnimation();

    // Use consistent accent color gradient
    const accentGradient = 'linear-gradient(90deg, #7B2CBF, #E31B88)';
    const accentColor = '#7B2CBF';

    // Filter friends based on search query and already added users
    const filteredFriends = useMemo(() => {
      return friends
        .filter(friend =>
          !currentCapsule?.access.includes(friend.username) &&
          (friend.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            friend.username.toLowerCase().includes(searchQuery.toLowerCase()))
        )
        .sort((a, b) => a.name.localeCompare(b.name));
    }, [friends, currentCapsule, searchQuery]);

    // Animation variants
    const containerVariants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.05,
          delayChildren: 0.1
        }
      },
      exit: {
        opacity: 0,
        transition: { duration: 0.2 }
      }
    };

    const itemVariants = {
      hidden: { y: 20, opacity: 0 },
      visible: {
        y: 0,
        opacity: 1,
        transition: { type: "spring", stiffness: 300, damping: 24 }
      }
    };

    const buttonVariants = {
      hover: {
        scale: 1.03,
        boxShadow: '0 4px 20px rgba(123, 44, 191, 0.3)',
        transition: { duration: 0.2 }
      },
      tap: { scale: 0.97 }
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    };

    const handleClose = () => {
      onClose()
      setSelectedTab(1)
    }

    const handleSubmit = () => {
      handleAddAccess()
      setSelectedTab(1)
    }

    const handleSelectUser = (username: string) => {
      if (selectedUsers.includes(username)) {
        setSelectedUsers(selectedUsers.filter(u => u !== username));
      } else {
        setSelectedUsers([...selectedUsers, username]);
      }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
      setSelectedTab(newValue);
    };

    // Threshold values for swipe detection
    const swipeThreshold = 50; // Minimum distance in pixels
    const velocityThreshold = 0.5; // Minimum velocity

    // Enhanced gesture handling with improved discrimination between swipes and scrolls
    const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const { offset, velocity } = info;

      // Calculate horizontal to vertical ratio to determine if this is primarily a horizontal gesture
      const horizontalRatio = Math.abs(offset.x) / (Math.abs(offset.y) + 0.1); // Adding 0.1 to avoid division by zero

      // Check if gesture is dominantly horizontal (ratio > 2 means more than 2x wider than tall)
      const isHorizontalGesture = horizontalRatio > 2;

      // Only process horizontal swipes with sufficient velocity or distance
      const hasMinimumForce = Math.abs(velocity.x) > velocityThreshold || Math.abs(offset.x) > swipeThreshold;

      if (isHorizontalGesture && hasMinimumForce) {
        // Get swipe direction (-1 for left, 1 for right)
        const direction = offset.x < 0 ? 1 : -1;

        // Calculate new tab index within bounds (0-2)
        const newIndex = Math.min(Math.max(selectedTab + direction, 0), 2);

        // Only change if it's a valid tab and (for non-owner) not trying to access owner-only tabs
        if (newIndex !== selectedTab && (isOwner || newIndex === 1)) {
          setSelectedTab(newIndex);
        }
      }

      // Always reset position with a spring animation
      controls.start({
        x: 0,
        transition: {
          type: "spring",
          stiffness: 300,
          damping: 30
        }
      });
    };

    // Get current capsule access list for display
    const currentAccessList = useMemo(() => {
      if (!currentCapsule) return [];

      return currentCapsule.access.map(username => {
        const friend = friends.find(f => f.username === username);
        return {
          username,
          name: friend?.name || username,
          picture: friend?.picture || ''
        };
      });
    }, [currentCapsule, friends]);

    // Check if user is owner
    const isOwner = currentCapsule?.owner === user?.username;

    // Update pin and pin-enabled status when dialog opens or currentCapsule changes
    useEffect(() => {
      if (currentCapsule) {
        setPinEnabled(!!currentCapsule.code);
        setCurrentPin(currentCapsule.code || '');
      }
    }, [currentCapsule, open]);

    // Generate PIN when QR tab is selected (only if we need a new PIN)
    useEffect(() => {
      if (selectedTab === 2 && isOwner && currentCapsule && !currentCapsule.code && !currentPin) {
        const newPin = Math.floor(1000 + Math.random() * 9000).toString();
        setPin(newPin);
      }
    }, [selectedTab, isOwner, currentCapsule, currentPin]);

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: isMobile ? 4 : 4,
            bgcolor: 'background.paper',
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))',
            backdropFilter: 'blur(20px)',
            border: isMobile ? 'none' : '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            height: isMobile ? '100%' : '600px',
            maxHeight: isMobile ? '90vh' : '90vh',
            // For mobile devices, maximize usable space
            ...(isMobile && {
              m: 0,
              width: '95%',
              maxWidth: '100%',
            }),
          },
        }}
      >
        <Box sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          zIndex: 1,
        }}>
          {/* Header */}
          <Box sx={{
            p: 3,
            pb: 2,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            position: 'relative',
          }}>
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <motion.div variants={itemVariants}>
                <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Share Time Capsule
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="body2" color="text.secondary" sx={{
                  opacity: 0.8,
                  maxWidth: '90%',
                }}>
                  {currentCapsule?.title ?
                    `Invite friends to contribute to "${currentCapsule.title}"` :
                    "Choose friends to share this memory with. They'll be able to view and contribute to the capsule."}
                </Typography>
              </motion.div>

              {!isOwner && (
                <motion.div
                  variants={itemVariants}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Alert
                    severity="info"
                    variant="filled"
                    sx={{
                      mt: 2,
                      bgcolor: 'rgba(123, 44, 191, 0.4)',
                      color: 'white',
                      '& .MuiAlert-icon': {
                        color: 'white'
                      }
                    }}
                  >
                    Only the capsule owner can share it with others
                  </Alert>
                </motion.div>
              )}
            </motion.div>
          </Box>

          {/* Tabs */}
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              '& .MuiTabs-indicator': {
                background: accentGradient,
                height: 3,
              },
              '& .MuiTab-root': {
                color: 'text.secondary',
                fontSize: isMobile ? '12px' : 'auto',
                '&.Mui-selected': {
                  color: 'white',
                  fontWeight: 500,
                },
              },
            }}
          >
            <Tab label="Add Friends" disabled={!isOwner} />
            <Tab label="Current Access" />
            <Tab label="QR Code" disabled={!isOwner} icon={<QrCode2 fontSize="small" />} iconPosition="start" />
          </Tabs>

          {/* Content */}
          <Box
            ref={tabContentRef}
            sx={{
              flex: 1,
              overflow: 'hidden', /* Changed from 'auto' to 'hidden' to prevent double scrollbars */
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={`tab-container-${selectedTab}`}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.1}
                onDragEnd={handleDragEnd}
                animate={controls}
                // Using dragDirectionLock to improve horizontal swiping experience
                dragDirectionLock={true}
                // Allow content to receive touch events for scrolling
                style={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  touchAction: 'pan-y' // Allow vertical touch scrolling while capturing horizontal drags
                }}
              >
                {selectedTab === 0 ? (
                  <motion.div
                    key="add-friends"
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={containerVariants}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Search input */}
                    <Box sx={{ p: 2, pb: 1 }}>
                      <motion.div variants={itemVariants}>
                        <TextField
                          fullWidth
                          placeholder="Search friends..."
                          variant="outlined"
                          value={searchQuery}
                          onChange={handleSearch}
                          disabled={!isOwner}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search sx={{ color: 'text.secondary' }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'rgba(0,0,0,0.1)',
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: 'divider',
                              },
                              '&:hover fieldset': {
                                borderColor: accentColor,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: accentColor,
                              },
                            },
                          }}
                        />
                      </motion.div>
                    </Box>

                    {/* Selected users */}
                    {selectedUsers.length > 0 && (
                      <Box sx={{ px: 2, py: 1 }}>
                        <motion.div variants={itemVariants}>
                          <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                          }}>
                            {selectedUsers.map(username => {
                              const friend = friends.find(f => f.username === username);
                              return (
                                <Chip
                                  key={username}
                                  label={friend?.name || username}
                                  avatar={
                                    <Avatar
                                      src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${username}/profilepic.jpeg`}
                                      alt={friend?.name || username}
                                    >
                                      {(friend?.name || username).charAt(0).toUpperCase()}
                                    </Avatar>
                                  }
                                  onDelete={() => handleSelectUser(username)}
                                  sx={{
                                    background: accentGradient,
                                    color: 'white',
                                    fontWeight: 500,
                                    '& .MuiChip-deleteIcon': {
                                      color: 'rgba(255,255,255,0.7)',
                                      '&:hover': {
                                        color: 'white',
                                      },
                                    },
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </motion.div>
                      </Box>
                    )}

                    {/* Friends list */}
                    <Box sx={{
                      flex: 1,
                      overflow: 'auto',
                      px: 1,
                      WebkitOverflowScrolling: 'touch',
                      msOverflowStyle: 'none',
                      scrollbarWidth: 'thin',
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '2px',
                      },
                      // Prevent content from being cut off at the bottom
                      pb: 2
                    }}>
                      {filteredFriends.length === 0 ? (
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          opacity: 0.7,
                          p: 3,
                        }}>
                          <PersonOff sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="body1" color="text.secondary" align="center">
                            {searchQuery ?
                              "No friends match your search" :
                              "No friends available to add"}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                            {searchQuery ?
                              "Try a different search term" :
                              "All your friends already have access to this capsule"}
                          </Typography>
                        </Box>
                      ) : (
                        <List sx={{ pt: 1 }}>
                          {filteredFriends.map((friend) => (
                            <motion.div
                              key={friend.username}
                              variants={itemVariants}
                            >
                              <ListItem
                                onClick={() => isOwner && handleSelectUser(friend.username)}
                                sx={{
                                  borderRadius: 2,
                                  mb: 1,
                                  transition: 'all 0.2s',
                                  cursor: isOwner ? 'pointer' : 'default',
                                  opacity: isOwner ? 1 : 0.6,
                                  '&:hover': {
                                    bgcolor: isOwner ? 'rgba(123, 44, 191, 0.1)' : 'transparent',
                                  },
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${friend.username}/profilepic.jpeg`}
                                    alt={friend.name}
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      border: selectedUsers.includes(friend.username) ?
                                        `2px solid ${accentColor}` :
                                        'none',
                                    }}
                                  >
                                    {friend.name.charAt(0).toUpperCase()}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={friend.name}
                                  secondary={`@${friend.username}`}
                                  primaryTypographyProps={{
                                    variant: 'subtitle1',
                                    fontWeight: selectedUsers.includes(friend.username) ? 600 : 400,
                                    color: selectedUsers.includes(friend.username) ?
                                      'white' :
                                      'text.primary',
                                  }}
                                  secondaryTypographyProps={{
                                    variant: 'body2',
                                    color: 'text.secondary',
                                  }}
                                />
                                {selectedUsers.includes(friend.username) && (
                                  <CheckCircle sx={{
                                    color: accentColor,
                                    ml: 1,
                                  }} />
                                )}
                              </ListItem>
                            </motion.div>
                          ))}
                        </List>
                      )}
                    </Box>
                  </motion.div>
                ) : selectedTab === 1 ? (
                  <motion.div
                    key="current-access"
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={containerVariants}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      overflow: 'hidden'
                    }}
                  >
                    <Box sx={{
                      flex: 1,
                      overflow: 'auto',
                      px: 1,
                      pt: 2,
                      WebkitOverflowScrolling: 'touch',
                      msOverflowStyle: 'none',
                      scrollbarWidth: 'thin',
                      '&::-webkit-scrollbar': {
                        width: '4px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '2px',
                      },
                      // Prevent content from being cut off at the bottom
                      pb: 2
                    }}>
                      {currentAccessList.length === 0 ? (
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: '100%',
                          opacity: 0.7,
                          p: 3,
                        }}>
                          <PersonOff sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="body1" color="text.secondary" align="center">
                            No one has access yet
                          </Typography>
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                            Share this capsule with friends to collaborate on memories
                          </Typography>
                        </Box>
                      ) : (
                        <List sx={{ pt: 1 }}>
                          {currentAccessList.map((person) => (
                            <motion.div
                              key={person.username}
                              variants={itemVariants}
                            >
                              <ListItem
                                sx={{
                                  borderRadius: 2,
                                  mb: 1,
                                  bgcolor: person.username === currentCapsule?.owner ?
                                    'rgba(123, 44, 191, 0.1)' :
                                    'transparent',
                                }}
                              >
                                <ListItemAvatar>
                                  <Avatar
                                    src={`https://timecapsule-profile-pictures.s3.ap-southeast-2.amazonaws.com/${person.username}/profilepic.jpeg`}
                                    alt={person.name}
                                    sx={{
                                      width: 48,
                                      height: 48,
                                      border: person.username === currentCapsule?.owner ?
                                        `2px solid ${accentColor}` :
                                        'none',
                                    }}
                                  >
                                    {person.name.charAt(0).toUpperCase()}
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={person.name}
                                  secondary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="body2" component="span" color="text.secondary">
                                        @{person.username}
                                      </Typography>
                                      {person.username === currentCapsule?.owner && (
                                        <Chip
                                          label="Owner"
                                          size="small"
                                          sx={{
                                            height: 20,
                                            fontSize: '0.7rem',
                                            background: accentGradient,
                                            color: 'white',
                                          }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  primaryTypographyProps={{
                                    variant: 'subtitle1',
                                    fontWeight: person.username === currentCapsule?.owner ? 600 : 400,
                                    color: person.username === currentCapsule?.owner ?
                                      accentColor :
                                      'text.primary',
                                  }}
                                />
                              </ListItem>
                            </motion.div>
                          ))}
                        </List>
                      )}
                    </Box>
                  </motion.div>
                ) : (
                  <motion.div
                    key="qr-code"
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={containerVariants}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      overflow: 'hidden',
                      padding: 0
                    }}
                  >
                    {!isOwner ? (
                      <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        opacity: 0.7,
                        p: 3,
                      }}>
                        <QrCode2 sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="body1" color="text.secondary" align="center">
                          QR Code Sharing
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                          Only the capsule owner can share via QR code
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        {/* QR Code Header with Toggle */}
                        <Box sx={{
                          p: 3,
                          pb: 2,
                          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <motion.div variants={itemVariants}>
                            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 500 }}>
                              QR Code Sharing
                            </Typography>
                          </motion.div>

                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <motion.div
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Switch
                                          checked={pinEnabled}
                                          disabled={user?.subscriptionStatus !== 'premium'}
                                          onChange={async (e) => {
                                            // Only allow premium users to enable sharing
                                            if (user?.subscriptionStatus !== 'premium') {
                                              setPinEnabled(false);
                                              return;
                                            }

                                            const enabled = e.target.checked;
                                            setPinEnabled(enabled);

                                            try {
                                              // Only make API request if enabling and no existing PIN
                                              if (enabled && !currentPin && !currentCapsule?.code) {
                                                const { tokens } = await fetchAuthSession();
                                                const authToken = tokens?.accessToken?.toString();
                                                const response = await fetch(
                                                  `https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/capsule/pin/${currentCapsule?.capsuleID}`,
                                                  {
                                                    method: 'POST',
                                                    headers: {
                                                      'Content-Type': 'application/json',
                                                      'x-api-key': authToken!
                                                    }
                                                  }
                                                );
                                                const data = await response.json();
                                                setCurrentPin(data.pin);
                                              }
                                            } catch (error) {
                                              console.error('Error enabling sharing:', error);
                                            }
                                          }}
                                          sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                              color: accentColor,
                                              '&:hover': {
                                                backgroundColor: 'rgba(123, 44, 191, 0.08)',
                                              },
                                            },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                              backgroundColor: accentColor,
                                            },
                                          }}
                                        />
                                      }
                                      label={
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {pinEnabled ? "Enabled" : "Disabled"}
                                        </Typography>
                                      }
                                    />
                                  </motion.div>
                                  
                                  {user?.subscriptionStatus !== 'premium' && (
                                    <Chip 
                                      label="Premium" 
                                      size="small"
                                      sx={{
                                        ml: 1,
                                        background: 'linear-gradient(90deg, #FFD700, #FFA500)',
                                        color: '#000',
                                        fontWeight: 600,
                                        fontSize: '0.7rem',
                                      }}
                                    />
                                  )}
                                </Box>
                        </Box>

                        {/* QR Code Content */}
                        {user?.subscriptionStatus !== 'premium' ? (
                          <Box sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 3,
                            gap: 3
                          }}>
                            <Box sx={{
                              width: 60,
                              height: 60,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 4px 20px rgba(255, 215, 0, 0.3)',
                              mb: 2
                            }}>
                              <QrCode2 sx={{ fontSize: 30, color: '#000' }} />
                            </Box>
                            
                            <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
                              QR Code Sharing is a Premium Feature
                            </Typography>
                            
                            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 400 }}>
                              Upgrade to Premium to share your capsules easily via QR codes. This allows friends to join your capsules by simply scanning the code.
                            </Typography>
                          </Box>
                        ) : pinEnabled ? (
                          <Box sx={{
                            flex: 1,
                            overflow: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            p: isMobile ? 2 : 3,
                            gap: 3,
                            height: '100%',
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            msOverflowStyle: 'none', /* Hide scrollbar IE and Edge */
                            scrollbarWidth: 'thin', /* Firefox */
                            // Custom scrollbar for Webkit browsers
                            '&::-webkit-scrollbar': {
                              width: '4px',
                            },
                            '&::-webkit-scrollbar-track': {
                              background: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                              background: 'rgba(255, 255, 255, 0.2)',
                              borderRadius: '2px',
                            },
                            // Improve momentum scrolling on iOS
                            '-webkit-overflow-scrolling': 'touch'
                          }}>
                            {/* QR Code Display */}
                            <motion.div
                              variants={itemVariants}
                              whileHover={{
                                scale: 1.02,
                                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
                              }}
                              style={{ marginTop: '16px' }}
                            >
                              <Paper sx={{
                                p: 3,
                                borderRadius: 3,
                                bgcolor: 'white',
                                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                position: 'relative',
                                overflow: 'hidden'
                              }}>
                                <Box sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: 'radial-gradient(circle at top right, rgba(123, 44, 191, 0.08), transparent 70%)',
                                  zIndex: 0
                                }} />

                                <Box sx={{ position: 'relative', zIndex: 1 }}>
                                  <QRCodeSVG
                                    value={`https://capsuletime.co?action=join&capsuleId=${currentCapsule?.capsuleID}`}
                                    size={isMobile ? 180 : 220}
                                    level="H"
                                    includeMargin={false}
                                    imageSettings={{
                                      src: "/favicon.ico",
                                      height: 40,
                                      width: 40,
                                      excavate: true,
                                    }}
                                  />
                                </Box>
                              </Paper>
                            </motion.div>

                            {/* Security PIN */}

                            <motion.div
                              variants={itemVariants}
                              style={{ width: '100%', maxWidth: '280px' }}
                            >
                              <Paper sx={{
                                borderRadius: 3,
                                overflow: 'hidden',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                background: 'linear-gradient(135deg, rgba(123, 44, 191, 0.15), rgba(227, 27, 136, 0.15))',
                                backdropFilter: 'blur(10px)',
                              }}>
                                <Box sx={{
                                  p: 1.5,
                                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                  bgcolor: 'rgba(0, 0, 0, 0.2)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 1
                                }}>
                                  <Typography variant="subtitle2" sx={{
                                    fontWeight: 600,
                                    color: 'white',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    fontSize: '0.75rem'
                                  }}>
                                    Security PIN
                                  </Typography>
                                </Box>
                                <Box sx={{
                                  p: 2,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  <Typography variant="h4" sx={{
                                    fontWeight: 700,
                                    letterSpacing: '0.25rem',
                                    fontFamily: 'monospace',
                                    color: 'white',
                                    textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                  }}>
                                    {currentPin || currentCapsule?.code}
                                  </Typography>
                                </Box>
                              </Paper>
                            </motion.div>


                            {/* Share Link */}
                            <motion.div
                              variants={itemVariants}
                              style={{ width: '100%', maxWidth: '400px' }}
                            >
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                width: '100%'
                              }}>
                                <Typography variant="subtitle2" sx={{
                                  color: 'text.secondary',
                                  fontWeight: 500
                                }}>
                                  Share Link
                                </Typography>

                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  width: '100%',
                                  position: 'relative'
                                }}>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    value={currentCapsule ? `https://capsuletime.co?action=join&capsuleId=${currentCapsule.capsuleID}` : ''}
                                    InputProps={{
                                      readOnly: true,
                                      sx: {
                                        borderRadius: 2,
                                        bgcolor: 'rgba(0, 0, 0, 0.1)',
                                        pr: 7, // Make room for the button
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: 'rgba(255, 255, 255, 0.1)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                          borderColor: 'rgba(255, 255, 255, 0.2)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                          borderColor: accentColor
                                        }
                                      }
                                    }}
                                    fullWidth
                                  />
                                  <Tooltip title={copied ? "Copied!" : "Copy link"} placement="top">
                                    <Button
                                      variant="contained"
                                      onClick={() => {
                                        if (currentCapsule) {
                                          navigator.clipboard.writeText(
                                            `https://capsuletime.co?action=join&capsuleId=${currentCapsule.capsuleID}`
                                          );
                                          setCopied(true);
                                          setTimeout(() => setCopied(false), 2000);
                                        }
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        right: 4,
                                        minWidth: 'auto',
                                        p: 1,
                                        borderRadius: 1.5,
                                        background: copied ? 'linear-gradient(90deg, #4CAF50, #2E7D32)' : accentGradient,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                          background: copied ? 'linear-gradient(90deg, #4CAF50, #2E7D32)' : accentGradient,
                                          filter: 'brightness(110%)'
                                        }
                                      }}
                                    >
                                      {copied ? <CheckCircle fontSize="small" /> : <ContentCopy fontSize="small" />}
                                    </Button>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </motion.div>

                            {/* Security Notice */}
                            <motion.div
                              variants={itemVariants}
                              style={{ width: '100%', maxWidth: '400px' }}
                            >
                              <Alert
                                severity="info"
                                variant="outlined"
                                icon={<QrCode2 fontSize="inherit" />}
                                sx={{
                                  borderRadius: 2,
                                  bgcolor: 'rgba(0, 0, 0, 0.1)',
                                  borderColor: 'rgba(255, 255, 255, 0.1)',
                                  '& .MuiAlert-icon': {
                                    color: accentColor
                                  }
                                }}
                              >
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Share this QR code carefully. Anyone with access can contribute to the capsule.
                                </Typography>
                              </Alert>
                            </motion.div>
                          </Box>
                        ) : (
                          <Box sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 3,
                            opacity: 0.7
                          }}>
                            <motion.div
                              variants={itemVariants}
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '16px',
                                maxWidth: '300px'
                              }}
                            >
                              <Box sx={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(123, 44, 191, 0.1)',
                                mb: 2
                              }}>
                                <QrCode2 sx={{ fontSize: 40, color: accentColor }} />
                              </Box>

                              <Typography variant="h6" align="center" sx={{ fontWeight: 500 }}>
                                Enable QR Code Sharing
                              </Typography>

                              <Typography variant="body2" color="text.secondary" align="center">
                                Toggle the switch above to generate a QR code that allows others to join this time capsule.
                              </Typography>
                            </motion.div>
                          </Box>
                        )}
                      </Box>
                    )}
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </Box>

          {/* Actions */}
          <Box sx={{
            p: 3,
            pt: 2,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            gap: 2,
          }}>
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              style={{ flex: 1, borderRadius: '24px' }}
            >
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClose}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: accentColor,
                    bgcolor: 'rgba(0,0,0,0.1)',
                  },
                }}
              >
                Cancel
              </Button>
            </motion.div>

            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              style={{ flex: 1, borderRadius: '24px' }}
            >
              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={!isOwner || selectedUsers.length === 0 || selectedTab !== 0}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  background: accentGradient,
                  '&:hover': {
                    background: accentGradient,
                    filter: 'brightness(110%)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.3)',
                  },
                }}
              >
                {selectedUsers.length > 0 && !isMobile ?
                  `Share with ${selectedUsers.length} friend${selectedUsers.length > 1 ? 's' : ''}` :
                  'Share Capsule'}
              </Button>
            </motion.div>
          </Box>
        </Box>
      </Dialog>
    );
  };

export const EditBioDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  bio: string;
  setBio: (bio: string) => void;
  wordCount: number;
}> = ({
  open,
  onClose,
  onConfirm,
  bio,
  setBio,
  wordCount,
}) => (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 8,
          bgcolor: 'background.paper',
          backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          Edit Bio
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Share a bit about yourself with your friends. Keep it concise and engaging.
        </Typography>

        <Box sx={{ position: 'relative' }}>
          <TextField
            autoFocus
            margin="dense"
            id="bio"
            label="Bio"
            type="text"
            fullWidth
            variant="outlined"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            multiline
            rows={4}
            error={wordCount > 20}
            helperText={wordCount > 20 ? "Bio cannot exceed 20 words." : `${wordCount}/20 words`}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.default',
                '& fieldset': {
                  borderColor: 'divider',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'primary.main',
                },
                '& .MuiInputBase-input': {
                  color: 'text.primary',
                },
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary',
                '&.Mui-focused': {
                  color: 'primary.main',
                },
              },
              '& .MuiFormHelperText-root': {
                color: wordCount > 20 ? 'error.main' : 'text.secondary',
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              right: 8,
              bottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: 'background.default',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: wordCount > 20 ? 'error.main' : 'text.secondary',
                fontWeight: wordCount > 20 ? 600 : 400
              }}
            >
              {wordCount}/20 words
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'background.default',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onConfirm}
            disabled={wordCount > 20}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
              },
            }}
          >
            Update Bio
          </Button>
        </Box>
      </Box>
    </Dialog>
  );

export const SelectCapsuleDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  availableCapsules: any[];
  selectedCapsule: string;
  setSelectedCapsule: (capsule: string) => void;
}> = ({
  open,
  onClose,
  onConfirm,
  availableCapsules,
  selectedCapsule,
  setSelectedCapsule,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const controls = useAnimation();

    // Use consistent accent color gradient from other dialogs
    const accentGradient = 'linear-gradient(90deg, #7B2CBF, #E31B88)';
    const accentColor = '#7B2CBF';

    // Animation variants
    const containerVariants = {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.05,
          delayChildren: 0.1
        }
      },
      exit: {
        opacity: 0,
        transition: { duration: 0.2 }
      }
    };

    const itemVariants = {
      hidden: { y: 20, opacity: 0 },
      visible: {
        y: 0,
        opacity: 1,
        transition: { type: "spring", stiffness: 300, damping: 24 }
      }
    };

    const cardVariants = {
      initial: { scale: 1 },
      hover: {
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
      },
      tap: { scale: 0.98 },

    };

    // Button animation variants
    const buttonVariants = {
      hover: {
        scale: 1.03,
        boxShadow: '0 4px 20px rgba(123, 44, 191, 0.3)',
        transition: { duration: 0.2 }
      },
      tap: { scale: 0.97 }
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 10,
            bgcolor: 'background.paper',
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))',
            backdropFilter: 'blur(20px)',
            border: isMobile ? 'none' : '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            // For mobile devices, maximize usable space
            ...(isMobile && {
              m: 0,
              width: '95%',
              maxWidth: '100%',
            }),
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            zIndex: 1,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              pb: 2,
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
              position: 'relative',
            }}
          >
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <motion.div variants={itemVariants}>
                <Typography variant="h5" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
                  Feature Capsule
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="body2" color="text.secondary" sx={{
                  opacity: 0.8,
                  maxWidth: '90%',
                }}>
                  Choose a capsule to feature on your profile. This will be visible to anyone who visits your profile.
                </Typography>
              </motion.div>
            </motion.div>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 3,
              pt: 2,
              maxHeight: isMobile ? 'calc(80vh - 200px)' : 'calc(90vh - 200px)', // Explicit max height to ensure scrolling
              WebkitOverflowScrolling: 'touch',
              msOverflowStyle: 'none',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '3px',
              },
              overflowY: 'auto', // Explicit vertical scrolling
            }}
          >
            <motion.div
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              {availableCapsules.length === 0 ? (
                <motion.div variants={itemVariants}>
                  <Box sx={{
                    p: 4,
                    bgcolor: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: 3,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    my: 4
                  }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        mb: 2
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h6" color="text.primary" fontWeight={500}>
                      No Capsules Available
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
                      You don't have any capsules available to feature. Create a new capsule to get started!
                    </Typography>
                  </Box>
                </motion.div>
              ) : (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? '140px' : '220px'}, 1fr))`,
                  gap: 2.5,
                  mt: 1,
                  pb: 4, // Add more padding at bottom for scrolling comfort
                  width: '100%',
                }}>
                  {availableCapsules.map((capsule, index) => (
                    <motion.div
                      key={capsule.capsuleID}
                      variants={itemVariants}
                      custom={index}
                      whileHover="hover"
                      whileTap="tap"
                      animate={selectedCapsule === capsule.capsuleID.toString() ? ["visible", "selected"] : "visible"}
                      onClick={() => setSelectedCapsule(capsule.capsuleID.toString())}
                    >
                      <motion.div variants={cardVariants}>
                        <Paper
                          elevation={0}
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            borderRadius: 3,
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0))',
                            border: '1px solid',
                            borderColor: selectedCapsule === capsule.capsuleID.toString()
                              ? accentColor
                              : 'rgba(255, 255, 255, 0.1)',
                            height: '100%',
                            transition: 'all 0.2s ease-in-out',
                            ...(selectedCapsule === capsule.capsuleID.toString() && {
                              boxShadow: `0 0 0 2px ${accentColor}`,
                            }),
                          }}
                        >
                          {/* Image */}
                          <Box
                            sx={{
                              position: 'relative',
                              paddingTop: '100%',
                              bgcolor: 'rgba(0, 0, 0, 0.2)',
                            }}
                          >
                            <Box
                              component="img"
                              src={capsule.imageUrl || "/polaroid.jpg"}
                              alt={capsule.title}
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.3s ease-out',
                              }}
                            />

                            {/* Image Count Badge */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                bgcolor: 'rgba(0, 0, 0, 0.6)',
                                borderRadius: '12px',
                                py: 0.5,
                                px: 1,
                                backdropFilter: 'blur(4px)',
                              }}
                            >
                              <ImageIcon sx={{ fontSize: 14, color: 'white' }} />
                              <Typography variant="caption" sx={{ color: 'white', fontWeight: 500 }}>
                                {capsule.images.length}
                              </Typography>
                            </Box>

                            {/* Selection Indicator */}
                            <AnimatePresence>
                              {selectedCapsule === capsule.capsuleID.toString() && (
                                <motion.div
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  exit={{ scale: 0, opacity: 0 }}
                                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                >
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      left: 8,
                                      bgcolor: accentColor,
                                      borderRadius: '50%',
                                      p: 1,
                                      boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                                  </Box>
                                </motion.div>
                              )}
                            </AnimatePresence>

                            {/* Gradient Overlay */}
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                p: 2,
                                background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.85))',
                                pt: 6, // Taller gradient for better text visibility
                              }}
                            >
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  color: 'white',
                                  fontWeight: 600,
                                  mb: 0.5,
                                  textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                                }}
                              >
                                {capsule.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.85)',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                  fontSize: '0.8rem',
                                }}
                              >
                                {capsule.description}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </motion.div>
                    </motion.div>
                  ))}
                </Box>
              )}
            </motion.div>
          </Box>

          {/* Actions */}
          <Box sx={{
            p: 3,
            pt: 2,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            gap: 2,
          }}>
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              style={{ flex: 1, borderRadius: '24px' }}
            >
              <Button
                fullWidth
                variant="outlined"
                onClick={onClose}
                sx={{
                  py: 1.5,
                  borderRadius: 6,
                  borderColor: 'divider',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: accentColor,
                    bgcolor: 'rgba(0,0,0,0.1)',
                  },
                }}
              >
                Cancel
              </Button>
            </motion.div>

            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              style={{ flex: 1, borderRadius: '24px' }}
            >
              <Button
                fullWidth
                variant="contained"
                onClick={onConfirm}
                disabled={!selectedCapsule}
                sx={{
                  py: 1.5,
                  borderRadius: 6,
                  background: accentGradient,
                  '&:hover': {
                    background: accentGradient,
                    filter: 'brightness(110%)',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.3)',
                  },
                }}
              >
                {isMobile ? ("Feature" ) : ("Feature Capsule")}
              </Button>
            </motion.div>
          </Box>
        </Box>
      </Dialog>
    );
  };

export const JoinCapsuleDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  capsuleId: string;
}> = ({ open, onClose, capsuleId }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleCloseJoinDialog = () => {
    // Clear both URL params and any stored join params in sessionStorage
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}`
    );
    
    // Clear any stored capsule join parameters from sessionStorage
    sessionStorage.removeItem('redirectAfterLogin');
    
    onClose();
  };

  const handleJoin = async () => {
    if (!pin.trim()) {
      setError('Please enter a PIN');
      return;
    }

    setIsLoading(true);
    try {
      const { tokens } = await fetchAuthSession();
      const authToken = tokens?.accessToken?.toString();
      
      if (!authToken) {
        throw new Error('Authentication failed');
      }

      const response = await fetch(
        `https://lfnawfdz0a.execute-api.ap-southeast-2.amazonaws.com/join/${capsuleId}`,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            'x-api-key': authToken 
          },
          body: JSON.stringify({ pin })
        }
      );

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(errorData);
      }

      // Clear any stored capsule join parameters from sessionStorage
      sessionStorage.removeItem('redirectAfterLogin');
      
      // Clear the URL parameters
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}`
      );
      
      // Refresh the page to show the newly joined capsule
      window.location.reload();
    } catch (err: any) {
      let errorMessage = 'Failed to join capsule';
      try {
        // Try to parse the error message if it's JSON
        const parsedError = JSON.parse(err.message);
        errorMessage = parsedError.message || errorMessage;
      } catch {
        // If not JSON, use the error message directly
        errorMessage = err.message || errorMessage;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseJoinDialog}
      maxWidth="xs"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 2,
          bgcolor: 'background.paper',
          backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Join Time Capsule
        </Typography>

        <TextField
          sx={{
            mt: 3,
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.23)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255, 255, 255, 0.87)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#7B2CBF',
                borderImage: 'linear-gradient(90deg, #7B2CBF, #E31B88) 1',
              },
            },
            '& .MuiInputLabel-outlined': {
              transform: 'translate(14px, -18px) scale(0.75)',
              '&.MuiInputLabel-shrink': {
                transform: 'translate(24px, -12px) scale(0.75)',
              },
            },
            '& .MuiInputLabel-outlined.Mui-focused': {
              color: '#7B2CBF',
            },
          }}
          fullWidth
          label="Enter PIN"
          variant="outlined"
          value={pin}
          onChange={(e) => {
            setPin(e.target.value.toUpperCase());
            setError('');
          }}
          error={!!error}
          helperText={error}
          InputProps={{
            inputMode: 'numeric',
            sx: { fontSize: 24, textAlign: 'center' }
          }}
        />

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCloseJoinDialog}
            sx={{
              py: 1.5,
              borderRadius: 2,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
                bgcolor: 'rgba(0,0,0,0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleJoin}
            disabled={pin.length === 0 || isLoading}
            sx={{
              py: 1.5,
              borderRadius: 2,
              background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
              '&:hover': {
                background: 'linear-gradient(90deg, #7B2CBF, #E31B88)',
                filter: 'brightness(110%)',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(255,255,255,0.1)',
                color: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            Join Capsule
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
